import type { CamelCase } from 'type-fest';
import { transformToCamelCase } from '../../helpers';
import { type TrackEventProperties, useAnalytics } from '../context';
import { WorkspaceRelatedTrackEventNames } from '../types';

type WorkspaceEventPayload = Pick<
  TrackEventProperties,
  'workspace' | 'workspaceMember' | 'workspaceId'
>;

type CamelCasedTrackFunction<T extends string> =
  `track${Capitalize<CamelCase<T>>}`;

type TrackEventPayload = (payload?: WorkspaceEventPayload) => void;

const originalEventNames = Object.keys(
  WorkspaceRelatedTrackEventNames
) as Array<keyof typeof WorkspaceRelatedTrackEventNames>;

const eventNamesCamelCased = Object.keys(
  transformToCamelCase(WorkspaceRelatedTrackEventNames, [])
).map((name) => `track${name[0].toUpperCase()}${name.slice(1)}`) as Array<
  CamelCasedTrackFunction<(typeof originalEventNames)[number]>
>;

const readOnlyEventNames = [...eventNamesCamelCased] as const;

export const useWorkspaceAnalytics = (location: string) => {
  const { trackEvent } = useAnalytics();

  const entries = originalEventNames.map((name, index) => [
    readOnlyEventNames[index],
    (payload?: WorkspaceEventPayload) => {
      trackEvent(WorkspaceRelatedTrackEventNames[name], {
        ...(payload ?? {}),
        location,
      });
    },
  ]) as Array<
    [
      CamelCasedTrackFunction<keyof typeof WorkspaceRelatedTrackEventNames>,
      TrackEventPayload,
    ]
  >;

  return Object.fromEntries(entries) as {
    [K in (typeof readOnlyEventNames)[number]]: TrackEventPayload;
  };
};
